import { cloudbaseApp } from './cloudbase'
import { demoAdminCall, demoEnabled } from './demo'
import type { AdminAction } from '../types'

interface ApiErrorShape {
  code: string
  message: string
}

interface ApiEnvelope<T> {
  ok: boolean
  data?: T
  error?: ApiErrorShape
}

export class AdminApiError extends Error {
  readonly code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'AdminApiError'
    this.code = code
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function parseUnknownResult(value: unknown): unknown {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value) as unknown
  } catch {
    throw new AdminApiError('INVALID_RESPONSE', '管理服务返回了无法解析的数据')
  }
}

export function parseAdminEnvelope<T>(value: unknown): ApiEnvelope<T> {
  const parsed = parseUnknownResult(value)
  if (!isRecord(parsed) || typeof parsed.ok !== 'boolean') {
    throw new AdminApiError('INVALID_RESPONSE', '管理服务响应格式不正确')
  }
  const errorValue = parsed.error
  const error = isRecord(errorValue)
    ? {
        code: typeof errorValue.code === 'string' ? errorValue.code : 'UNKNOWN_ERROR',
        message: typeof errorValue.message === 'string' ? errorValue.message : '后台操作失败',
      }
    : undefined
  return { ok: parsed.ok, data: parsed.data as T | undefined, error }
}

export async function callAdmin<T>(
  action: AdminAction,
  payload: Record<string, unknown> = {},
): Promise<T> {
  if (demoEnabled) return demoAdminCall<T>(action)
  const response: unknown = await cloudbaseApp.callFunction({
    name: 'treasure-api',
    data: { action, payload },
    parse: true,
  })
  if (!isRecord(response)) throw new AdminApiError('INVALID_RESPONSE', '云函数没有返回结果')
  const envelope = parseAdminEnvelope<T>(response.result)
  if (!envelope.ok || typeof envelope.data === 'undefined') {
    throw new AdminApiError(
      envelope.error?.code || 'UNKNOWN_ERROR',
      envelope.error?.message || '后台操作失败',
    )
  }
  return envelope.data
}

export function requestId(prefix: string): string {
  return `${prefix}-${Date.now()}-${crypto.randomUUID()}`
}
